import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

type AdminHeaderProps = {
  breadcrumbs: Array<{ label: string; href?: string }>;
  title: string;
  onPreview?: () => void;
  onSave?: () => void;
  onSaveAndPublish?: () => void;
  onUnpublish?: () => void;
  onWriteByAI?: () => void;
  isSaving?: boolean;
  previewUrl?: string;
  isPublished?: boolean;
};

export function AdminHeader({
  breadcrumbs,
  title,
  onPreview,
  onSave,
  onSaveAndPublish,
  onUnpublish,
  onWriteByAI,
  isSaving = false,
  previewUrl,
  isPublished = false,
}: AdminHeaderProps) {
  const handlePreview = () => {
    if (previewUrl) {
      window.open(previewUrl, "_blank");
    } else if (onPreview) {
      onPreview();
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {crumb.href ? (
                  <Link to={crumb.href} className="hover:text-gray-700">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900 font-medium">{crumb.label}</span>
                )}
                {index < breadcrumbs.length - 1 && (
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </React.Fragment>
            ))}
          </nav>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>
        <div className="flex items-center space-x-3">
          {onWriteByAI && (
            <Button
              variant="outline"
              onClick={onWriteByAI}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Write by AI
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview
          </Button>
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-yellow hover:bg-yellow-500 border border-brand-yellow shadow-sm transition-colors rounded-lg disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V2" />
            </svg>
            {isSaving ? "Đang lưu..." : "Save"}
          </button>
          {isPublished ? (
            <button
              type="button"
              onClick={onUnpublish}
              disabled={isSaving}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-yellow hover:bg-yellow-500 border border-brand-yellow shadow-sm transition-colors rounded-lg disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              {isSaving ? "Đang lưu..." : "Unpublish"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onSaveAndPublish}
              disabled={isSaving}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-yellow hover:bg-yellow-500 border border-brand-yellow shadow-sm transition-colors rounded-lg disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              {isSaving ? "Đang lưu..." : "Save & Publish"}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}